import { create } from 'zustand'
import { db, type Project } from '@/lib/db'
import { useEditorStore } from './editor-store'
import { useRecordingStore } from './recording-store'

const isDesktopApp = () => !!(window as any).electron

// ── Helpers for desktop (filesystem) path ────────────────────────────────────

/** Build the plain-JSON metadata object that goes into project.json on disk */
function buildDiskMeta(id: string, name: string): any {
    const e = useEditorStore.getState()
    const r = useRecordingStore.getState()
    return {
        id,
        name,
        createdAt: Date.now(),
        lastModified: Date.now(),
        isDesktop: r.isDesktop,
        isExtension: r.isExtension,
        screenWidth: r.screenWidth,
        screenHeight: r.screenHeight,
        duration: e.duration,
        aspectRatio: e.aspectRatio,
        backgroundValue: e.backgroundValue,
        backgroundType: e.backgroundType,
        padding: e.padding,
        borderRadius: e.borderRadius,
        shadow: e.shadow,
        perspective: e.perspective,
        borderWidth: e.borderWidth,
        borderColor: e.borderColor,
        borderOpacity: e.borderOpacity,
        animationIn: e.animationIn,
        animationOut: e.animationOut,
        cameraLayer: e.cameraLayer,
        zoomRegions: e.zoomRegions,
        clips: e.clips,
        videoCrop: e.videoCrop,
        cursorSettings: e.cursorSettings,
    }
}

async function blobToBuffer(blob: Blob | null | undefined): Promise<ArrayBuffer | null> {
    if (!blob) return null
    return blob.arrayBuffer()
}

// ── Store ─────────────────────────────────────────────────────────────────────

interface ProjectState {
    projects: Project[]
    currentProjectId: string | null
    isSidebarOpen: boolean

    loadProjects: () => Promise<void>
    createProject: (name?: string) => Promise<string>
    loadProject: (id: string) => Promise<void>
    deleteProject: (id: string) => Promise<void>
    deleteAllProjects: () => Promise<void>
    renameProject: (id: string, name: string) => Promise<void>
    saveCurrentProject: () => Promise<void>
    setIsSidebarOpen: (isOpen: boolean) => void
}

export const useProjectStore = create<ProjectState>((set, get) => ({
    projects: [],
    currentProjectId: null,
    isSidebarOpen: false,

    setIsSidebarOpen: (isOpen) => set({ isSidebarOpen: isOpen }),

    // ── loadProjects ──────────────────────────────────────────────────────────
    loadProjects: async () => {
        if (isDesktopApp()) {
            const metas: any[] = await (window as any).electron.listDiskProjects()
            // Convert to the Project shape the UI expects (no blobs at list time)
            const projects = metas.map((m) => ({
                ...m,
                videoBlob: undefined,
                cameraBlob: undefined,
            })) as unknown as Project[]
            set({ projects })
        } else {
            const projects = await db.projects.toArray()
            set({ projects: projects.sort((a, b) => b.lastModified - a.lastModified) })
        }
    },

    // ── createProject ─────────────────────────────────────────────────────────
    createProject: async (name = 'Untitled Project') => {
        const id = crypto.randomUUID()
        const recordingState = useRecordingStore.getState()

        if (isDesktopApp()) {
            const meta = { ...buildDiskMeta(id, name), createdAt: Date.now() }

            // Set ID immediately so the editor can reference this project
            // before the (potentially slow) video disk-write completes
            set({ currentProjectId: id })

            const [videoBuffer, cameraBuffer] = await Promise.all([
                blobToBuffer(recordingState.blob),
                blobToBuffer(recordingState.cameraBlob),
            ])
            await (window as any).electron.saveDiskProject(
                meta,
                videoBuffer,
                cameraBuffer,
                recordingState.mouseData,
            )
            await get().loadProjects()
            return id
        }

        // ── Web path (IndexedDB) ──────────────────────────────────────────────
        const editorState = useEditorStore.getState()
        const newProject: Project = {
            id,
            name,
            createdAt: Date.now(),
            lastModified: Date.now(),
            duration: editorState.duration,
            videoBlob: recordingState.blob || undefined,
            cameraBlob: recordingState.cameraBlob || undefined,
            aspectRatio: editorState.aspectRatio,
            backgroundValue: editorState.backgroundValue,
            backgroundType: editorState.backgroundType,
            padding: editorState.padding,
            borderRadius: editorState.borderRadius,
            shadow: editorState.shadow,
            perspective: editorState.perspective,
            borderWidth: editorState.borderWidth,
            borderColor: editorState.borderColor,
            borderOpacity: editorState.borderOpacity,
            animationIn: editorState.animationIn,
            animationOut: editorState.animationOut,
            cameraLayer: editorState.cameraLayer,
            zoomRegions: editorState.zoomRegions,
            clips: editorState.clips,
            videoCrop: editorState.videoCrop,
            mouseData: recordingState.mouseData,
            isDesktop: recordingState.isDesktop,
            isExtension: recordingState.isExtension,
            cursorSettings: editorState.cursorSettings,
        }

        await db.projects.add(newProject)
        await get().loadProjects()
        set({ currentProjectId: id })
        return id
    },

    // ── loadProject ───────────────────────────────────────────────────────────
    loadProject: async (id) => {
        if (isDesktopApp()) {
            const result = await (window as any).electron.loadDiskProject(id)
            if (!result) return
            const { meta, videoBuffer, cameraBuffer, cursorData } = result

            const { setBlob, setCameraBlob, setMouseData, setDesktop, setExtension, setScreenDimensions } =
                useRecordingStore.getState()

            if (videoBuffer) setBlob(new Blob([videoBuffer], { type: 'video/webm' }))
            if (cameraBuffer) setCameraBlob(new Blob([cameraBuffer], { type: 'video/webm' }))
            if (cursorData?.length) setMouseData(cursorData)
            setDesktop(meta.isDesktop ?? false)
            setExtension(meta.isExtension ?? false)
            if (meta.screenWidth && meta.screenHeight)
                setScreenDimensions(meta.screenWidth, meta.screenHeight)

            const editorStore = useEditorStore.getState()
            editorStore.reset()

            const duration = meta.duration || (meta.clips && meta.clips.length > 0 ? Math.max(...meta.clips.map((c: any) => c.end)) : 0)
            useRecordingStore.getState().setDuration(duration)
            editorStore.setDuration(duration)

            editorStore.setAspectRatio(meta.aspectRatio)
            editorStore.setBackground(meta.backgroundType, meta.backgroundValue)
            editorStore.setPadding(meta.padding)
            editorStore.setBorderRadius(meta.borderRadius)
            editorStore.setShadow(meta.shadow)
            editorStore.setPerspective(meta.perspective)
            editorStore.setBorderWidth(meta.borderWidth)
            editorStore.setBorderColor(meta.borderColor)
            editorStore.setBorderOpacity(meta.borderOpacity ?? 1)
            editorStore.setAnimationIn(meta.animationIn)
            editorStore.setAnimationOut(meta.animationOut)
            if (meta.cameraLayer) editorStore.updateCameraLayer(meta.cameraLayer)

            useEditorStore.setState({
                zoomRegions: meta.zoomRegions ?? [],
                clips: meta.clips ?? [],
                videoCrop: meta.videoCrop ?? null,
                ...(meta.cursorSettings ? { cursorSettings: meta.cursorSettings } : {}),
                currentTime: 0,
                isPlaying: false,
            })

            set({ currentProjectId: id })
            return
        }

        // ── Web path ──────────────────────────────────────────────────────────
        const project = await db.projects.get(id)
        if (!project) return

        const { setBlob, setCameraBlob, setMouseData, setDesktop, setExtension } =
            useRecordingStore.getState()
        if (project.videoBlob) {
            setBlob(project.videoBlob instanceof ArrayBuffer ? new Blob([project.videoBlob], { type: 'video/webm' }) : project.videoBlob)
        }
        if (project.cameraBlob) {
            setCameraBlob(project.cameraBlob instanceof ArrayBuffer ? new Blob([project.cameraBlob], { type: 'video/webm' }) : project.cameraBlob)
        }
        if (project.mouseData) setMouseData(project.mouseData)
        setDesktop(project.isDesktop ?? false)
        setExtension(project.isExtension ?? false)

        const editorStore = useEditorStore.getState()
        editorStore.reset()

        const duration = project.duration || (project.clips && project.clips.length > 0 ? Math.max(...project.clips.map(c => c.end)) : 0)
        useRecordingStore.getState().setDuration(duration)
        editorStore.setDuration(duration)

        editorStore.setAspectRatio(project.aspectRatio)
        editorStore.setBackground(project.backgroundType, project.backgroundValue)
        editorStore.setPadding(project.padding)
        editorStore.setBorderRadius(project.borderRadius)
        editorStore.setShadow(project.shadow)
        editorStore.setPerspective(project.perspective)
        editorStore.setBorderWidth(project.borderWidth)
        editorStore.setBorderColor(project.borderColor)
        editorStore.setBorderOpacity(project.borderOpacity ?? 1)
        editorStore.setAnimationIn(project.animationIn)
        editorStore.setAnimationOut(project.animationOut)
        if (project.cameraLayer) editorStore.updateCameraLayer(project.cameraLayer)

        useEditorStore.setState({
            zoomRegions: project.zoomRegions,
            clips: project.clips,
            videoCrop: project.videoCrop,
            ...(project.cursorSettings ? { cursorSettings: project.cursorSettings } : {}),
            currentTime: 0,
            isPlaying: false,
        })

        set({ currentProjectId: id })
    },

    // ── deleteProject ─────────────────────────────────────────────────────────
    deleteProject: async (id) => {
        if (isDesktopApp()) {
            await (window as any).electron.deleteDiskProject(id)
        } else {
            await db.projects.delete(id)
        }
        await get().loadProjects()
        if (get().currentProjectId === id) set({ currentProjectId: null })
    },

    // ── deleteAllProjects ─────────────────────────────────────────────────────
    deleteAllProjects: async () => {
        if (isDesktopApp()) {
            const { projects } = get()
            await Promise.all(projects.map((p) => (window as any).electron.deleteDiskProject(p.id)))
        } else {
            await db.projects.clear()
        }
        set({ projects: [], currentProjectId: null })
    },

    // ── renameProject ─────────────────────────────────────────────────────────
    renameProject: async (id, name) => {
        if (isDesktopApp()) {
            await (window as any).electron.renameDiskProject(id, name)
        } else {
            await db.projects.update(id, { name, lastModified: Date.now() })
        }
        await get().loadProjects()
    },

    // ── saveCurrentProject ────────────────────────────────────────────────────
    saveCurrentProject: async () => {
        const id = get().currentProjectId
        if (!id) return

        const recordingState = useRecordingStore.getState()

        if (isDesktopApp()) {
            const existingProjects = get().projects
            const existing = existingProjects.find((p) => p.id === id)
            const meta = {
                ...buildDiskMeta(id, existing?.name ?? 'Untitled Project'),
                createdAt: (existing as any)?.createdAt ?? Date.now(),
                lastModified: Date.now(),
            }
            // On save, only re-write video if blob changed (it's large); always update cursor+settings
            const [videoBuffer, cameraBuffer] = await Promise.all([
                blobToBuffer(recordingState.blob),
                blobToBuffer(recordingState.cameraBlob),
            ])
            await (window as any).electron.saveDiskProject(
                meta,
                videoBuffer,
                cameraBuffer,
                recordingState.mouseData,
            )
            await get().loadProjects()
            return
        }

        // ── Web path ──────────────────────────────────────────────────────────
        const editorState = useEditorStore.getState()
        await db.projects.update(id, {
            lastModified: Date.now(),
            duration: editorState.duration,
            videoBlob: recordingState.blob || undefined,
            cameraBlob: recordingState.cameraBlob || undefined,
            aspectRatio: editorState.aspectRatio,
            backgroundValue: editorState.backgroundValue,
            backgroundType: editorState.backgroundType,
            padding: editorState.padding,
            borderRadius: editorState.borderRadius,
            shadow: editorState.shadow,
            perspective: editorState.perspective,
            borderWidth: editorState.borderWidth,
            borderColor: editorState.borderColor,
            borderOpacity: editorState.borderOpacity,
            animationIn: editorState.animationIn,
            animationOut: editorState.animationOut,
            cameraLayer: editorState.cameraLayer,
            zoomRegions: editorState.zoomRegions,
            clips: editorState.clips,
            videoCrop: editorState.videoCrop,
            mouseData: recordingState.mouseData,
            isDesktop: recordingState.isDesktop,
            isExtension: recordingState.isExtension,
            cursorSettings: editorState.cursorSettings,
        })

        await get().loadProjects()
    }
}))
